import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { collaborationPermissions } from '../lib/collaborationPermissions';
import { createLogger } from '../lib/logger';

const log = createLogger('collaboration');

interface PermissionCheckResult {
  canEdit: boolean;
  role: 'owner' | 'collaborator';
}

interface CollaborationPermissionsHook {
  canEdit: boolean;
  canDelete: boolean;
  role: 'owner' | 'collaborator' | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

/**
 * 統一協作權限檢查 Hook
 * 提供快取機制和自動刷新功能
 */
export const useCollaborationPermissions = (invitationId: string | null): CollaborationPermissionsHook => {
  const { user } = useAuth();
  const [permissions, setPermissions] = useState<PermissionCheckResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const checkPermissions = useCallback(async () => {
    if (!invitationId || !user?.id) {
      setPermissions(null);
      setLoading(false);
      setError(null);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const [canEdit, isOwner] = await Promise.all([
        collaborationPermissions.canEdit(user.id, invitationId),
        collaborationPermissions.isOwner(user.id, invitationId)
      ]);
      
      const result: PermissionCheckResult = {
        canEdit,
        role: isOwner ? 'owner' : 'collaborator'
      };
      
      setPermissions(result);
    } catch (err) {
      log.error('權限檢查失敗:', err);
      setError('無法檢查操作權限，請稍後再試');
      setPermissions(null);
    } finally {
      setLoading(false);
    }
  }, [invitationId, user?.id]);

  // 初始載入和依賴變化時重新檢查
  useEffect(() => {
    checkPermissions();
  }, [checkPermissions]);

  // 手動重新整理權限
  const refresh = useCallback(async () => {
    await checkPermissions();
  }, [checkPermissions]);

  return {
    canEdit: permissions?.canEdit || false,
    canDelete: permissions?.role === 'owner', // 只有擁有者可以刪除
    role: permissions?.role || null,
    loading,
    error,
    refresh
  };
};

export default useCollaborationPermissions;